import { memo, useId, SVGProps } from 'react';
import { SVGCircle, SVGGroup, SVGText, SVGGroupProps } from './styleableSvgComponents';
import { cn } from '@ifixit/ui/lib/utils';

type BasicClusterCircleProps = {
   fill?: string;
   hoverFill?: string;
   focusFill?: string;
   className?: string;
} & Omit<SVGProps<SVGCircleElement>, 'fill' | 'cx' | 'cy' | 'r'>;

type BasicClusterTextProps = {
   fill?: string;
   hoverFill?: string;
   focusFill?: string;
   dy?: string;
   fontSize?: string;
   fontWeight?: string;
   className?: string;
} & Omit<SVGProps<SVGTextElement>, 'fill'>;

type BasicClusterProps = {
   scaledX: number;
   scaledY: number;
   text: string;
   innerCircleProps?: BasicClusterCircleProps;
   outerCircleProps?: BasicClusterCircleProps;
   textProps?: BasicClusterTextProps;
   groupProps?: SVGGroupProps;
};

export const BasicCluster = memo(function BasicClusterInternal({
   scaledX,
   scaledY,
   text,
   innerCircleProps,
   outerCircleProps,
   textProps,
   groupProps,
}: BasicClusterProps) {
   const uniqueId = useId();
   const outerCircleId = `outer-circle-${uniqueId}`;
   const innerCircleId = `inner-circle-${uniqueId}`;
   const textId = `text-${uniqueId}`;

   const {
      fill: outerFill,
      hoverFill: outerHoverFill,
      focusFill: outerFocusFill,
      className: outerClassName,
      ...outerRest
   } = outerCircleProps || {};
   const {
      fill: innerFill,
      hoverFill: innerHoverFill,
      focusFill: innerFocusFill,
      className: innerClassName,
      ...innerRest
   } = innerCircleProps || {};
   const {
      fill: textFill,
      hoverFill: textHoverFill,
      focusFill: textFocusFill,
      dy,
      fontSize,
      fontWeight,
      className: textClassName,
      ...textRest
   } = textProps || {};

   return (
      <SVGGroup
         {...groupProps}
         className={cn(
            'group cursor-pointer outline-none focus:outline-none',
            groupProps?.className
         )}
      >
         <SVGCircle
            {...outerRest}
            id={outerCircleId}
            x={scaledX}
            y={scaledY}
            r={24}
            fill={outerFill}
            className={cn('transition-colors', outerClassName)}
         />
         <SVGCircle
            {...innerRest}
            id={innerCircleId}
            x={scaledX}
            y={scaledY}
            r={20}
            fill={innerFill}
            className={cn('transition-colors', innerClassName)}
         />
         <SVGText
            {...textRest}
            id={textId}
            x={scaledX}
            y={scaledY}
            textAnchor="middle"
            fill={textFill}
            dy={dy}
            fontSize={fontSize}
            fontWeight={fontWeight}
            className={cn('transition-colors pointer-events-none', textClassName)}
         >
            {text}
         </SVGText>
         <style>{`
            g.group:hover #${CSS.escape(outerCircleId)},
            g.group:focus #${CSS.escape(outerCircleId)} {
               fill: ${outerHoverFill || outerFill};
            }
            g.group:hover #${CSS.escape(innerCircleId)},
            g.group:focus #${CSS.escape(innerCircleId)} {
               fill: ${innerHoverFill || innerFill};
            }
            g.group:hover #${CSS.escape(textId)},
            g.group:focus #${CSS.escape(textId)} {
               fill: ${textHoverFill || textFill};
            }
         `}</style>
      </SVGGroup>
   );
});
